import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { RenderMarkdownWithLatex } from '../../utils/utils';

const parseSolution = (text) => {
    if (!text) return [];

    // Rozszerzenie ma nagłówki "### 🔹 Krok N: Tytuł", podstawa "Krok N: ### 🔹 Tytuł".
    // Przestawiamy PR do formatu podstawy: "Krok N:" jako marker kroku (pasek rysuje
    // stepper), a "### 🔹 Tytuł" jako nagłówek h3 w treści kroku — identycznie jak PP.
    const cleaned = text.replace(
        /#{1,6}[ \t]*(\p{Extended_Pictographic}?)[ \t]*(Krok\s*\d+\s*:)[ \t]*([^\n]*)/giu,
        (m, emoji, krok, title) => {
            const t = title.trim();
            const e = emoji || '🔹';
            return t ? `${krok} ### ${e} ${t}` : krok;
        }
    );

    // Dzielimy TYLKO na nagłówkach na początku linii. Inaczej "Krok N:" wspomniane
    // w treści (np. plan w "Krok 0: Strategia": "1. **Krok 1: ...**") tworzyłoby
    // fałszywe kroki i osierocone `**`.
    const parts = cleaned.split(/(?=^[ \t]*Krok\s*\d+\s*:)/im);

    return parts.map((part) => {
        const trimmed = part.trim();
        if (!trimmed) return null;

        const stepMatch = trimmed.match(/^Krok\s*(\d+)\s*:/i);

        if (stepMatch) {
            return {
                type: 'step',
                content: trimmed.replace(stepMatch[0], '').trim(),
                number: stepMatch[1]
            };
        } else {
            return {
                type: 'intro',
                content: trimmed,
                number: null
            };
        }
    }).filter(Boolean);
};

const SolutionStepper = ({ solutionText }) => {
    const steps = useMemo(() => parseSolution(solutionText), [solutionText]);

    if (!steps || steps.length === 0) return null;

    return (
        <div className="w-full mt-2 space-y-4">
            {steps.map((item, idx) => (
                <StepItem key={idx} item={item} index={idx} />
            ))}
        </div>
    );
};

const StepItem = ({ item, index }) => {
    const isIntro = item.type === 'intro';

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.05 }}
            className={`w-full rounded-xl border overflow-hidden ${isIntro ? "bg-gray-50 border-gray-200" : "bg-white border-gray-200 shadow-sm"}`}
        >
            {!isIntro && (
                <div className="bg-[#233855] px-4 py-1.5 flex items-center">
                    <span className="text-xs font-bold font-apotek uppercase tracking-widest text-white">
                        Krok {item.number}
                    </span>
                </div>
            )}

            <div className={`px-4 pb-4 text-sm text-gray-700 leading-relaxed overflow-x-auto [&_h3]:!mt-2 [&_h3]:!mb-1 [&>*:first-child]:!mt-0 ${isIntro ? "italic text-gray-600 pt-3" : "pt-2"}`}>
                <RenderMarkdownWithLatex text={item.content} />
            </div>
        </motion.div>
    );
};

export default SolutionStepper;